import { useState } from 'react';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';
import { animated, useTransition } from '@react-spring/web';

import {
  type SlideContent,
  SliderImageAndContent,
} from 'Apps/Consultation/Components/SliderImageAndContent';

import { Typography } from 'Components/Typography';

import { handleKeyPress } from 'utils/a11y';

const SlidersContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  width: 100%;
`;

const Slide = styled(animated.div)`
  grid-row: 1 / -1;
  grid-column: 1 / -1;
  width: 100%;
  height: 100%;
`;

const SlidersControls = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  width: 100%;
`;

const SlidersControlsButton = styled(Typography)`
  color: ${theme.colors.primary[400]};
  cursor: pointer;

  &.active {
    font-weight: bold;
    color: ${theme.colors.neutral[900]};
    border-bottom: 2px solid ${theme.colors.neutral[900]};
  }
`;

const SlidersLegend = styled.div`
  padding: ${legacyTheme.spacing.s32} 0;
  height: 160px;

  ${legacyTheme.breakpoints.up('md')} {
    height: 132px;
  }
`;

export type Slider = {
  sliderBodyLegend: string;
  slidesContent: SlideContent[];
  sliderId: string;
  sliderName: string;
  sliderNameTitle: string;
  sliderTitleLegend: string;
};

type SliderOfSliderProps = {
  heapDomain?: string;
  isOnScreen?: boolean;
  sliders: Slider[];
};

export const SliderOfSlider = ({
  heapDomain,
  isOnScreen = false,
  sliders,
}: SliderOfSliderProps) => {
  const [index, setIndex] = useState(0);
  const opacityTransitions = useTransition(index, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  return (
    <SlidersContent>
      <>
        {opacityTransitions((props, item) => {
          const { sliderId, slidesContent, sliderBodyLegend, sliderTitleLegend } =
            sliders[item] ?? {};
          return (
            <Slide
              key={sliderId}
              aria-labelledby={sliderId}
              id={`slide-${sliderId}`}
              role="tabpanel"
              style={props}
            >
              <>
                {sliderBodyLegend && (
                  <SlidersLegend>
                    {sliderTitleLegend && (
                      <Typography align="center" bold variant="p2">
                        {sliderTitleLegend}
                      </Typography>
                    )}

                    <Typography align="center" variant="p2">
                      {sliderBodyLegend}
                    </Typography>
                  </SlidersLegend>
                )}
                {slidesContent && (
                  <SliderImageAndContent
                    heapDomain={heapDomain}
                    isOnScreen={isOnScreen}
                    slidesContent={slidesContent}
                  />
                )}
              </>
            </Slide>
          );
        })}

        <SlidersControls data-testid="slider-slider-controls" role="tablist">
          {sliders.map(({ sliderId, sliderName, sliderNameTitle }, i) => {
            return (
              <SlidersControlsButton
                key={sliderId}
                align="center"
                aria-controls={`slide-${sliderId}`}
                aria-label={`Navigate to ${sliderId} slide`}
                aria-selected={i === index}
                className={i === index ? 'active' : undefined}
                id={sliderId}
                onClick={() => setIndex(i)}
                onKeyPress={handleKeyPress(() => setIndex(i))}
                role="tab"
                tabIndex="0"
                variant="p3"
              >
                {sliderNameTitle && (
                  <>
                    <span>{sliderNameTitle}</span>
                    <br />
                  </>
                )}
                {sliderName}
              </SlidersControlsButton>
            );
          })}
        </SlidersControls>
      </>
    </SlidersContent>
  );
};
